import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  Divider,
  Chip,
  Alert,
  SelectChangeEvent,
  Autocomplete
} from '@mui/material';
import Download from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { CountyData, CropData } from '../services/countyService';
import { webScrapingService } from '../services/webScrapingService';
import { MarketIntelligenceService, type MarketData } from '../services/marketIntelligenceService';
import useChartRegistration from '../components/charts/ChartRegistration';
import ChartComponent from '../components/charts/chart';
import { useAuth } from '../context/AuthContext';

// Create unique IDs for each chart
const CHART_IDS = {
  marketPrice: 'market-price-chart',
  cropComparison: 'crop-comparison-chart',
  marketTrend: 'market-trend-chart',
  international: 'international-comparison-chart'
};

// Full list of Kenyan counties for the dropdown
const AVAILABLE_COUNTIES = [
  'Mombasa', 'Kwale', 'Kilifi', 'Tana River', 'Lamu', 'Taita-Taveta',
  'Garissa', 'Wajir', 'Mandera', 'Marsabit', 'Isiolo', 'Meru', 'Tharaka-Nithi',
  'Embu', 'Kitui', 'Machakos', 'Makueni', 'Nyandarua', 'Nyeri', 'Kirinyaga',
  "Murang'a", 'Kiambu', 'Turkana', 'West Pokot', 'Samburu', 'Trans Nzoia',
  'Uasin Gishu', 'Elgeyo-Marakwet', 'Nandi', 'Baringo', 'Laikipia', 'Nakuru',
  'Narok', 'Kajiado', 'Kericho', 'Bomet', 'Kakamega', 'Vihiga', 'Bungoma',
  'Busia', 'Siaya', 'Kisumu', 'Homa Bay', 'Migori', 'Kisii', 'Nyamira',
  'Nairobi'
];

const AVAILABLE_CROPS = [
  'Maize', 'Beans', 'Potatoes', 'Wheat', 'Rice', 'Sorghum', 'Millet',
  'Coffee', 'Tea', 'Sugarcane', 'Cotton', 'Vegetables', 'Bananas',
  'Avocado', 'Green grams', 'Sweet potatoes', 'Cassava'
];

const AgriDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { isRegistered: chartJsReady, error: chartJsError } = useChartRegistration();
  const isAdmin = (() => {
    const candidates = [
      user?.role,
      (user as any)?.user_metadata?.role,
      (user as any)?.app_metadata?.role,
    ]
      .map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : ''))
      .filter(Boolean);
    return candidates.some((r) => r === 'admin' || r === 'super_admin' || r === 'superadmin');
  })();
  
  // Chart instance refs to manage cleanup
  const chartRefs = useRef({
    marketPrice: null,
    cropComparison: null,
    marketTrend: null,
    international: null
  });
  
  // Check URL parameters for initial values
  const queryParams = new URLSearchParams(location.search);
  const countyFromUrl = queryParams.get('county');
  const cropFromUrl = queryParams.get('crop');

  const [countyQuery, setCountyQuery] = useState(countyFromUrl || 'Kiambu');
  const [cropQuery, setCropQuery] = useState(cropFromUrl || 'Maize');
  const [selectedCounty, setSelectedCounty] = useState<CountyData | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<CropData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileWarnings, setProfileWarnings] = useState<string[]>([]);
  const [availableCounties] = useState(AVAILABLE_COUNTIES);
  const [availableCrops] = useState(AVAILABLE_CROPS);
  const [chartsError, setChartsError] = useState(false);
  const [dataSource, setDataSource] = useState<string>('Supabase-backed sources');
  const [sourceCitations, setSourceCitations] = useState<string[]>([]);
  const [marketData, setMarketData] = useState<MarketData[] | null>(null);
  const [priceSource, setPriceSource] = useState<'official' | 'marketplace' | 'ncpb' | 'blended'>('official');
  const [kamisLoading, setKamisLoading] = useState(false);
  const [kamisError, setKamisError] = useState<string | null>(null);
  const [kamisRows, setKamisRows] = useState<any[]>([]);
  const [kamisIngesting, setKamisIngesting] = useState(false);
  const [kamisIngestMessage, setKamisIngestMessage] = useState<string | null>(null);
  const [kamisUploadDetails, setKamisUploadDetails] = useState<
    { fileName: string; parsedRows: number; error?: string }[]
  >([]);

  const normalizeCropName = (name: string) => name.toLowerCase().trim();

  const isMatchingMarketplaceProduct = (productName: string, cropName: string) => {
    const normalizedProduct = normalizeCropName(productName);
    const normalizedCrop = normalizeCropName(cropName);

    if (normalizedProduct === normalizedCrop) return true;
    if (normalizedProduct.startsWith(`${normalizedCrop} `)) return true;
    if (normalizedProduct.endsWith(` ${normalizedCrop}`)) return true;
    if (normalizedProduct.includes(` ${normalizedCrop} `)) return true;

    const aliasMap: Record<string, string[]> = {
      maize: ['corn'],
      beans: ['dry beans', 'common beans'],
      potatoes: ['irish potatoes'],
      rice: [],
      wheat: []
    };

    const aliases = aliasMap[normalizedCrop] || [];
    if (aliases.some(alias => normalizedProduct === alias || normalizedProduct.startsWith(`${alias} `))) {
      return true;
    }

    return false;
  };

  // Show chart error if Chart.js failed to register
  useEffect(() => {
    if (chartJsError) {
      setChartsError(true);
      console.error("Chart.js registration error:", chartJsError);
    }
  }, [chartJsError]);
  
  // Cleanup chart instances on unmount
  useEffect(() => {
    return () => {
      // Destroy all chart instances on component unmount
      Object.values(chartRefs.current).forEach(chart => {
        if (chart) {
          // @ts-ignore - chart has a destroy method
          chart.destroy();
        }
      });
    };
  }, []);

  // Load initial data
  useEffect(() => {
    if (chartJsReady) {
      loadData();
    }
    
    // Add cleanup function
    return () => {
      setSelectedCounty(null);
      setSelectedCrop(null);
    };
  }, [chartJsReady]);

  // Load data when URL parameters change
  useEffect(() => {
    if (countyFromUrl && cropFromUrl) {
      setCountyQuery(countyFromUrl);
      setCropQuery(cropFromUrl);
      if (chartJsReady) {
        // Clear any existing chart instances before loading new data from URL
        Object.values(chartRefs.current).forEach(chart => {
          if (chart) {
            // @ts-ignore - chart has a destroy method
            chart.destroy();
          }
        });
        
        // Reset refs
        chartRefs.current = {
          marketPrice: null,
          cropComparison: null,
          marketTrend: null,
          international: null
        };
        
        loadData();
      }
    }
  }, [location.search, chartJsReady]);

  useEffect(() => {
    if (!selectedCounty?.name) {
      setKamisRows([]);
      setKamisError(null);
      return;
    }
    const loadKamis = async () => {
      try {
        setKamisError(null);
        setKamisLoading(true);
        const resp = await webScrapingService.getKamisMarketPricesByCounty(selectedCounty.name, 14, 250);
        setKamisRows(Array.isArray((resp as any)?.rows) ? (resp as any).rows : []);
      } catch (e) {
        setKamisRows([]);
        setKamisError(e instanceof Error ? e.message : 'Failed to load KAMIS market prices');
      } finally {
        setKamisLoading(false);
      }
    };
    void loadKamis();
  }, [selectedCounty?.name]);

  const loadData = async (overrideCounty?: string, overrideCrop?: string) => {
    setLoading(true);
    setError(null);
    setChartsError(false);
    setSourceCitations([]);
    setProfileWarnings([]);
    setMarketData(null);
    setKamisIngestMessage(null);

    const countyName = overrideCounty ?? countyQuery;
    const cropName = overrideCrop ?? cropQuery;

    try {
      console.log(`Loading agronomic data: County=${countyName}, Crop=${cropName}`);
      setDataSource('Supabase-backed county/crop profiles (when available) and official KAMIS uploads for market prices');
      
      let countyData: CountyData | null = null;
      const nextWarnings: string[] = [];

      if (countyName) {
        try {
          countyData = await webScrapingService.getCountyByName(countyName);
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : 'County profile could not be loaded. Ask an admin to populate the county dataset.';
          nextWarnings.push(message);
          countyData = {
            name: countyName,
            region: 'Not available',
            climate: {
              temperature: 'Not available',
              rainfall: 'Not available',
              altitude: 'Not available',
            },
            mainCrops: [],
            governmentRequirements: {
              licenses: [],
              permits: [],
              regulations: [],
            },
            marketPrices: [],
            farmingTips: [],
            resources: [],
            lastUpdated: new Date(),
            dataSource: 'Not available',
            sourceCitations: ['County profile not available in the database yet.'],
          };
        }
        setSelectedCounty(countyData);
        const citations = countyData?.sourceCitations;
        if (citations) {
          setSourceCitations(prev => [...prev, ...citations]);
        }
      }

      if (cropName) {
        let cropData: CropData | null = null;
        try {
          cropData = await webScrapingService.getCropData(cropName);
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : 'Crop profile could not be loaded. Ask an admin to populate the crop encyclopedia dataset.';
          nextWarnings.push(message);
          cropData = {
            cropName,
            countyData: {},
            nationalAverage: 0,
            internationalComparison: {},
            marketTrend: [],
            growingConditions: {
              temperature: 'Not available',
              rainfall: 'Not available',
              soil: 'Not available',
              altitude: 'Not available',
            },
            bestPractices: [],
            dataSource: 'Not available',
            sourceCitations: ['Crop profile not available in the database yet.'],
          };
        }
        setSelectedCrop(cropData);
        if (cropData.sourceCitations) {
          setSourceCitations(prev => [...prev, ...cropData.sourceCitations!]);
        }
      }

      if (cropName && countyName) {
        const marketResults = await MarketIntelligenceService.getMarketData(
          cropName,
          countyName
        );
        const realMarketResults = (marketResults || []).filter(entry => entry.source !== 'mock');

        const hasRealSupabaseData =
          realMarketResults.length > 0;

        if (!hasRealSupabaseData) {
          nextWarnings.push(
            'No real marketplace market-price records are available for this crop and county yet. Upload KAMIS exports for official prices or publish marketplace price records to populate this view.',
          );
        }

        setMarketData(realMarketResults);
      }

      setProfileWarnings(nextWarnings);
    } catch (err) {
      console.error("Data loading error:", err);
      setError(`Failed to load agronomic data. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCountyChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setCountyQuery(newValue || '');
  };

  const handleCropChange = (event: React.SyntheticEvent, newValue: string | null) => {
    setCropQuery(newValue || '');
  };

  const handlePriceSourceChange = (event: SelectChangeEvent<'official' | 'marketplace' | 'ncpb' | 'blended'>) => {
    const value = event.target.value as 'official' | 'marketplace' | 'ncpb' | 'blended';
    setPriceSource(value);
  };

  const runSearch = (overrideCounty?: string, overrideCrop?: string) => {
    Object.values(chartRefs.current).forEach(chart => {
      if (chart) {
        // @ts-ignore - chart has a destroy method
        chart.destroy();
      }
    });
    
    // Reset refs
    chartRefs.current = {
      marketPrice: null,
      cropComparison: null,
      marketTrend: null,
      international: null
    };
    
    loadData(overrideCounty, overrideCrop);
  };

  const handleSearch = () => {
    runSearch();
  };

  const handleUploadKamis = async (files: FileList | File[]) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) return;

    try {
      setKamisIngestMessage(null);
      setKamisError(null);
      setKamisIngesting(true);
      const list = Array.from(files as any).filter(Boolean) as File[];
      const parsedAll: any[] = [];
      const details: { fileName: string; parsedRows: number; error?: string }[] = [];
      for (const f of list) {
        try {
          const parsed = await webScrapingService.parseKamisExportFile(f);
          parsedAll.push(...parsed);
          details.push({ fileName: f.name, parsedRows: parsed.length });
        } catch (e) {
          details.push({
            fileName: f.name,
            parsedRows: 0,
            error: e instanceof Error ? e.message : 'Failed to parse file',
          });
        }
      }
      setKamisUploadDetails(details);
      const parsedFiles = details.filter((d) => !d.error).length;
      const failedFiles = details.filter((d) => d.error).length;
      if (parsedAll.length === 0) {
        throw new Error(
          failedFiles > 0
            ? `No rows were parsed. ${failedFiles} file(s) failed to parse.`
            : 'No rows were parsed from the selected files.',
        );
      }
      const result = await webScrapingService.ingestKamisMarketRows(parsedAll);
      setKamisIngestMessage(
        `Uploaded ${list.length} file(s). Parsed: ${parsedAll.length} rows (${parsedFiles} ok, ${failedFiles} failed). Stored: ${(result as any)?.inserted ?? parsedAll.length}`,
      );
      if (selectedCounty?.name) {
        const resp = await webScrapingService.getKamisMarketPricesByCounty(selectedCounty.name, 14, 250);
        setKamisRows(Array.isArray((resp as any)?.rows) ? (resp as any).rows : []);
      }
    } catch (e) {
      setKamisIngestMessage(null);
      setKamisError(e instanceof Error ? e.message : 'Failed to upload file');
    } finally {
      setKamisIngesting(false);
    }
  };

  const handleExport = () => {
    if (!selectedCounty || !selectedCrop) return;

    const officialPrice = selectedCounty.marketPrices.find(
      p => p.commodity.toLowerCase() === selectedCrop.cropName.toLowerCase()
    );

    const marketplaceEntries = (marketData || []).filter(
      entry =>
        entry.source === 'marketplace' &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
    );

    const ncpbEntries = (marketData || []).filter(
      entry =>
        entry.source === 'ncpb' &&
        entry.location === selectedCounty.name &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
    );

    const marketplaceAverage =
      marketplaceEntries.length > 0
        ? marketplaceEntries.reduce((sum, entry) => sum + entry.price, 0) /
          marketplaceEntries.length
        : null;

    const ncpbAverage =
      ncpbEntries.length > 0
        ? ncpbEntries.reduce((sum, entry) => sum + entry.price, 0) / ncpbEntries.length
        : null;

    const effectivePrice =
      priceSource === 'official'
        ? officialPrice?.price ?? marketplaceAverage
        : priceSource === 'marketplace'
        ? marketplaceAverage ?? officialPrice?.price
        : priceSource === 'ncpb'
        ? ncpbAverage
        : officialPrice && marketplaceAverage
        ? (officialPrice.price + marketplaceAverage) / 2
        : officialPrice?.price ?? marketplaceAverage;

    const effectiveUnit =
      priceSource === 'ncpb'
        ? ncpbEntries[0]?.unit ?? ''
        : officialPrice?.unit || (marketplaceEntries[0]?.unit ?? '');

    const csvContent = [
      ['Report', 'Agricultural Planning Dashboard'],
      ['Date', new Date().toLocaleDateString()],
      ['County', selectedCounty.name],
      ['Crop', selectedCrop.cropName],
      [],
      ['Market Prices'],
      ['Commodity', 'Price', 'Unit', 'Date'],
      ...selectedCounty.marketPrices.map(p => [p.commodity, p.price, p.unit, p.date]),
      [],
      ['Crop Performance'],
      ['County', 'Score'],
      ...Object.entries(selectedCrop.countyData).map(([c, s]) => [c, s]),
      [],
      ['Price Analysis'],
      ['Price Source Preference', priceSource],
      [
        'Official Price (if available)',
        officialPrice ? `${officialPrice.price} per ${officialPrice.unit} (as of ${officialPrice.date})` : 'N/A'
      ],
      [
        'Marketplace Average Price',
        marketplaceAverage && marketplaceEntries.length > 0
          ? `${marketplaceAverage.toFixed(2)} per ${marketplaceEntries[0].unit} (${marketplaceEntries.length} records)`
          : 'N/A'
      ],
      [
        'NCPB Reference Price',
        ncpbAverage && ncpbEntries.length > 0
          ? `${ncpbAverage.toFixed(2)} per ${ncpbEntries[0].unit} (${ncpbEntries.length} records)`
          : 'N/A'
      ],
      [
        'Effective Price Used',
        effectivePrice
          ? `${effectivePrice.toFixed(2)}${effectiveUnit ? ` per ${effectiveUnit}` : ''}`
          : 'N/A'
      ]
    ].map(e => e.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedCounty.name}_${selectedCrop.cropName}_report.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Chart generation functions with error handling
  const renderMarketPriceChart = () => {
    if (!selectedCounty?.marketPrices || selectedCounty.marketPrices.length === 0) {
      return <Alert severity="info">No market price data available</Alert>;
    }

    const data = {
      labels: selectedCounty.marketPrices.map(price => price.commodity),
      datasets: [
        {
          label: 'Market Prices (KES)',
          data: selectedCounty.marketPrices.map(price => price.price),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="bar"
        data={data}
        id={`market-price-${selectedCounty.name}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `Market Prices in ${selectedCounty.name}`
            }
          }
        }}
      />
    );
  };

  const renderCropComparisonChart = () => {
    if (!selectedCrop?.countyData) {
      return <Alert severity="info">No crop comparison data available</Alert>;
    }

    const data = {
      labels: Object.keys(selectedCrop.countyData),
      datasets: [
        {
          label: `${selectedCrop.cropName} Performance Score`,
          data: Object.values(selectedCrop.countyData),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="bar"
        data={data}
        id={`crop-comparison-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `${selectedCrop.cropName} Performance by County`
            }
          }
        }}
      />
    );
  };

  const renderMarketTrendChart = () => {
    if (!selectedCrop?.marketTrend || selectedCrop.marketTrend.length === 0) {
      return <Alert severity="info">No market trend data available</Alert>;
    }

    const data = {
      labels: selectedCrop.marketTrend.map(trend => trend.month),
      datasets: [
        {
          label: `${selectedCrop.cropName} Price Trend (KES)`,
          data: selectedCrop.marketTrend.map(trend => trend.price),
          fill: false,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1
        }
      ]
    };

    return (
      <ChartComponent
        type="line"
        data={data}
        id={`market-trend-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: `${selectedCrop.cropName} Price Trends`
            }
          }
        }}
      />
    );
  };

  const renderInternationalComparisonChart = () => {
    if (!selectedCrop?.internationalComparison) {
      return <Alert severity="info">No international comparison data available</Alert>;
    }

    const data = {
      labels: Object.keys(selectedCrop.internationalComparison),
      datasets: [
        {
          label: 'Performance Score',
          data: Object.values(selectedCrop.internationalComparison),
          backgroundColor: [
            'rgba(255, 99, 132, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 206, 86, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    return (
      <ChartComponent
        type="pie"
        data={data}
        id={`international-comparison-${selectedCrop.cropName}`}
        options={{
          plugins: {
            title: {
              display: true,
              text: 'International Comparison'
            }
          }
        }}
      />
    );
  };

  const generateSuitabilityAnalysis = () => {
    if (!selectedCounty || !selectedCrop) return null;

    try {
      const isMainCrop = selectedCounty.mainCrops.includes(selectedCrop.cropName);
      const countyYieldScore = selectedCrop.countyData[selectedCounty.name];
      const comparedToNational = countyYieldScore > selectedCrop.nationalAverage ? 'Above' : 'Below';

      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Suitability Analysis: {selectedCrop.cropName} in {selectedCounty.name}
            </Typography>
            
            {isMainCrop ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {selectedCrop.cropName} is a main crop in {selectedCounty.name} County
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {selectedCrop.cropName} is not listed as a main crop in {selectedCounty.name} County
              </Alert>
            )}

            {countyYieldScore ? (
              <Typography variant="body1" gutterBottom>
                Yield Score: {countyYieldScore}/100 ({comparedToNational} national average of {selectedCrop.nationalAverage})
              </Typography>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No yield data available for {selectedCrop.cropName} in {selectedCounty.name} County
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Climate Compatibility
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  <strong>County Temperature:</strong> {selectedCounty.climate.temperature}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Crop Temperature Needs:</strong> {selectedCrop.growingConditions.temperature}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" gutterBottom>
                  <strong>County Rainfall:</strong> {selectedCounty.climate.rainfall}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Crop Water Needs:</strong> {selectedCrop.growingConditions.rainfall}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    } catch (error) {
      console.error("Error generating suitability analysis:", error);
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Suitability Analysis
            </Typography>
            <Alert severity="error">
              Failed to generate suitability analysis. Please try again.
            </Alert>
          </CardContent>
        </Card>
      );
    }
  };

  const generateMarketAnalysis = () => {
    if (!selectedCounty || !selectedCrop) return null;

    try {
      const kamisForCrop = (kamisRows || []).filter((r: any) =>
        typeof r?.commodity === 'string' &&
        isMatchingMarketplaceProduct(String(r.commodity), selectedCrop.cropName)
      );
      const latestKamisDate = kamisForCrop.reduce((max: string, r: any) => {
        const d = typeof r?.price_date === 'string' ? r.price_date : '';
        return d && d > max ? d : max;
      }, '');
      const latestKamisRows = latestKamisDate
        ? kamisForCrop.filter((r: any) => r?.price_date === latestKamisDate)
        : [];
      const kamisPrices = latestKamisRows
        .map((r: any) => (typeof r?.retail_price === 'number' ? r.retail_price : typeof r?.wholesale_price === 'number' ? r.wholesale_price : null))
        .filter((n: any) => typeof n === 'number' && Number.isFinite(n)) as number[];
      const kamisAverage = kamisPrices.length > 0 ? kamisPrices.reduce((a, b) => a + b, 0) / kamisPrices.length : null;
      const kamisUnit = latestKamisRows.find((r: any) => typeof r?.unit === 'string' && r.unit.trim())?.unit ?? '';

      const officialPrice = selectedCounty.marketPrices.find(
        p => p.commodity.toLowerCase() === selectedCrop.cropName.toLowerCase()
      );

      const realEntries = (marketData || []).filter(entry =>
        (entry.source === 'marketplace' ||
          entry.source === 'external' ||
          entry.source === 'government' ||
          entry.source === 'news' ||
          entry.source === 'ncpb') &&
        entry.location === selectedCounty.name &&
        isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const realAverage =
        realEntries.length > 0
          ? realEntries.reduce((sum, entry) => sum + entry.price, 0) /
            realEntries.length
          : null;

      const ncpbEntries = (marketData || []).filter(
        entry =>
          entry.source === 'ncpb' &&
          entry.location === selectedCounty.name &&
          isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const ncpbAverage =
        ncpbEntries.length > 0
          ? ncpbEntries.reduce((sum, entry) => sum + entry.price, 0) / ncpbEntries.length
          : null;

      const effectivePrice =
        priceSource === 'official'
          ? kamisAverage ?? officialPrice?.price ?? realAverage ?? ncpbAverage
          : priceSource === 'marketplace'
          ? realAverage ?? officialPrice?.price ?? kamisAverage ?? ncpbAverage
          : priceSource === 'ncpb'
          ? ncpbAverage
          : officialPrice && realAverage
          ? (officialPrice.price + realAverage) / 2
          : kamisAverage ?? officialPrice?.price ?? realAverage ?? ncpbAverage;

      const effectiveUnit =
        priceSource === 'ncpb'
          ? ncpbEntries[0]?.unit ||
            officialPrice?.unit ||
            realEntries[0]?.unit ||
            ''
          : priceSource === 'official'
          ? kamisUnit || officialPrice?.unit || realEntries[0]?.unit || ''
          : officialPrice?.unit ||
            realEntries[0]?.unit ||
            '';

      const entriesForSelectedCropAndCounty = (marketData || []).filter(
        entry =>
          entry.location === selectedCounty.name &&
          isMatchingMarketplaceProduct(entry.product_name, selectedCrop.cropName)
      );

      const sourceBreakdown = entriesForSelectedCropAndCounty.reduce(
        (acc, entry) => {
          const key = entry.source;
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {} as Record<MarketData['source'], number | undefined>
      );

      const totalWeight =
        (officialPrice ? 0.45 : 0) +
        (kamisAverage ? 0.35 : 0) +
        (realEntries.length > 0 ? 0.2 : 0);

      const adoptionPercentage =
        totalWeight > 0
          ? Math.round(
              ((officialPrice ? 0.45 : 0) + (kamisAverage ? 0.35 : 0) + (realEntries.length > 0 ? 0.2 : 0)) /
                totalWeight *
                100
            )
          : 0;

      const isSyntheticNcpb = (marketData || []).some(entry => entry.id.startsWith('county-'));

      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Market Analysis
            </Typography>
            
            {effectivePrice ? (
              <>
                <Typography variant="body1" gutterBottom>
                  Current price in {selectedCounty.name}: {effectivePrice.toFixed(2)} KES
                  {effectiveUnit && ` per ${effectiveUnit}`}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Source preference:{' '}
                  {priceSource === 'official'
                    ? 'Official statistics, preferring KAMIS and county references'
                    : priceSource === 'marketplace'
                    ? 'Marketplace trades with official references when marketplace data is limited'
                    : priceSource === 'ncpb'
                    ? 'NCPB reference'
                    : 'Blended view using available official and marketplace references'}
                </Typography>
                {officialPrice && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Official reference: {officialPrice.price} KES per {officialPrice.unit} (as of {officialPrice.date})
                  </Typography>
                )}
                {kamisAverage && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    KAMIS reference: {kamisAverage.toFixed(2)} KES{effectiveUnit ? ` per ${effectiveUnit}` : ''}{latestKamisDate ? ` (as of ${latestKamisDate})` : ''}
                  </Typography>
                )}
                {realAverage && realEntries.length > 0 && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Market/news average ({realEntries.length} records):{' '}
                    {realAverage.toFixed(2)} KES per {realEntries[0].unit}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Data coverage: {adoptionPercentage}% sourced from available official and market records in the current view.
                </Typography>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No price data available for {selectedCrop.cropName} in {selectedCounty.name} County
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Data snapshot for {selectedCrop.cropName} in {selectedCounty.name}:
              </Typography>
              <Typography variant="caption" color="textSecondary" component="div">
                Total entries: {entriesForSelectedCropAndCounty.length} | 
                Marketplace: {sourceBreakdown.marketplace || 0} · 
                External: {sourceBreakdown.external || 0} · 
                Government: {sourceBreakdown.government || 0} · 
                NCPB: {sourceBreakdown.ncpb || 0} {isSyntheticNcpb && <Chip label="Live Scrape" size="small" color="success" variant="outlined" sx={{ height: 16, fontSize: '0.65rem', ml: 0.5 }} />} · 
                News: {sourceBreakdown.news || 0}
              </Typography>
              {isSyntheticNcpb && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                  * Showing live scraped data from NCPB portal as no historical records found in database.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Planning Recommendations
            </Typography>
            <ul>
              <li>Optimal planting time based on rainfall patterns: {selectedCrop.cropName === 'Maize' ? 'March-April (long rains) and October-November (short rains)' : 'Varies by crop'}</li>
              <li>Consider selling {selectedCrop.cropName} during {selectedCrop.marketTrend.reduce((max, current, idx, arr) => current.price > arr[max].price ? idx : max, 0) === 0 ? 'January' : selectedCrop.marketTrend.reduce((max, current, idx, arr) => current.price > arr[max].price ? idx : max, 0) + 1} when prices tend to be highest</li>
              <li>Expected yield per hectare: {selectedCrop.cropName === 'Maize' ? '1.5-3.5 tons' : selectedCrop.cropName === 'Coffee' ? '0.5-1.5 tons' : selectedCrop.cropName === 'Tea' ? '8-10 tons of green leaf' : selectedCrop.cropName === 'Rice' ? '3.5-5 tons' : 'Varies by crop'}</li>
            </ul>
          </CardContent>
        </Card>
      );
    } catch (error) {
      console.error("Error generating market analysis:", error);
      return (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Market Analysis
            </Typography>
            <Alert severity="error">
              Failed to generate market analysis. Please try again.
            </Alert>
          </CardContent>
        </Card>
      );
    }
  };

  // Render fallback content if charts have errors
  if (chartsError) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
            Agricultural Planning Dashboard
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            There was an error rendering the charts. This may be due to browser compatibility issues.
          </Alert>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Reload Page
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Agricultural Planning Dashboard
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Comprehensive information to help farmers make informed decisions
        </Typography>

        {/* Search Section */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Autocomplete
                value={countyQuery}
                onChange={handleCountyChange}
                options={availableCounties}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select County"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                value={cropQuery}
                onChange={handleCropChange}
                options={availableCrops}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Crop"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth sx={{ minWidth: 120 }}>
                <InputLabel id="price-source-label">Price Source</InputLabel>
                <Select
                  labelId="price-source-label"
                  value={priceSource}
                  label="Price Source"
                  onChange={handlePriceSourceChange}
                >
                  <MenuItem value="official">Official stats</MenuItem>
                  <MenuItem value="marketplace">Marketplace trades</MenuItem>
                  <MenuItem value="ncpb">NCPB reference</MenuItem>
                  <MenuItem value="blended">Blended view</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : 'Update'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={handleExport}
                disabled={loading || !selectedCounty || !selectedCrop}
                startIcon={<Download />}
                sx={{ height: '56px' }}
              >
                Export
              </Button>
            </Grid>
            {isAdmin && (
              <Grid item xs={12} md={2}>
                <input
                  id="kamis-upload-agri-dashboard"
                  type="file"
                  accept=".xls,.xlsx,.html,.htm"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files;
                    e.target.value = '';
                    if (!f || f.length === 0) return;
                    void handleUploadKamis(f);
                  }}
                />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    const el = document.getElementById('kamis-upload-agri-dashboard') as HTMLInputElement | null;
                    el?.click();
                  }}
                  disabled={loading || kamisIngesting}
                  startIcon={<UploadIcon />}
                  sx={{ height: '56px' }}
                >
                  {kamisIngesting ? 'Uploading…' : 'Upload KAMIS file'}
                </Button>
              </Grid>
            )}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {profileWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {profileWarnings.join(' | ')}
            </Alert>
          )}
          {kamisIngestMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {kamisIngestMessage}
            </Alert>
          )}
          {kamisError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {kamisError}
            </Alert>
          )}
          {kamisUploadDetails.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {kamisUploadDetails
                .map((d) => `${d.fileName}: ${d.error ? `error (${d.error})` : `${d.parsedRows} rows`}`)
                .slice(0, 6)
                .join(' | ')}
              {kamisUploadDetails.length > 6 ? ` | +${kamisUploadDetails.length - 6} more` : ''}
            </Alert>
          )}
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Analysis Section */}
            {selectedCounty && selectedCrop && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  {generateSuitabilityAnalysis()}
                </Grid>
                <Grid item xs={12} md={6}>
                  {generateMarketAnalysis()}
                </Grid>
              </Grid>
            )}

            {/* County Information */}
            {selectedCounty && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom color="primary">
                  {selectedCounty.name} County Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Climate & Geographic Data
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>Region:</strong> {selectedCounty.region}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Temperature:</strong> {selectedCounty.climate.temperature}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Rainfall:</strong> {selectedCounty.climate.rainfall}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            <strong>Altitude:</strong> {selectedCounty.climate.altitude}
                          </Typography>
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          Main Crops
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedCounty.mainCrops.map((crop, index) => (
                            <Chip 
                              key={index} 
                              label={crop} 
                              color={crop === selectedCrop?.cropName ? "primary" : "default"} 
                              variant={crop === selectedCrop?.cropName ? "filled" : "outlined"} 
                              onClick={() => {
                                setCropQuery(crop);
                                runSearch(undefined, crop);
                              }}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Market Prices in {selectedCounty.name}
                        </Typography>
                        {renderMarketPriceChart()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Farming Tips for {selectedCounty.name} County
                        </Typography>
                        <Grid container spacing={2}>
                          {selectedCounty.farmingTips.map((tipCategory, index) => (
                            <Grid item xs={12} md={6} key={index}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                  {tipCategory.category}
                                </Typography>
                                <ul>
                                  {tipCategory.tips.map((tip, tipIndex) => (
                                    <li key={tipIndex}>{tip}</li>
                                  ))}
                                </ul>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Crop Information */}
            {selectedCrop && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" gutterBottom color="primary">
                  {selectedCrop.cropName} Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {selectedCrop.cropName} Performance by County
                        </Typography>
                        {renderCropComparisonChart()}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            <strong>National Average:</strong> {selectedCrop.nationalAverage}/100
                          </Typography>
                          {selectedCounty && selectedCrop.countyData[selectedCounty.name] && (
                            <Typography variant="body2" gutterBottom>
                              <strong>{selectedCounty.name} Score:</strong> {selectedCrop.countyData[selectedCounty.name]}/100
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {selectedCrop.cropName} Market Price Trend (Annual)
                        </Typography>
                        {renderMarketTrendChart()}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Growing Conditions for {selectedCrop.cropName}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          {Object.entries(selectedCrop.growingConditions).map(([condition, value], index) => (
                            <Typography key={index} variant="body2" gutterBottom>
                              <strong>{condition.charAt(0).toUpperCase() + condition.slice(1)}:</strong> {value}
                            </Typography>
                          ))}
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          Best Practices
                        </Typography>
                        <ul>
                          {selectedCrop.bestPractices.map((practice, index) => (
                            <li key={index}>{practice}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          International Comparison
                        </Typography>
                        {renderInternationalComparisonChart()}
                        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                          Data Source: {selectedCrop.dataSource}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Data Sources & Citations */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Data Source: {dataSource}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This dashboard integrates official agronomic and economic statistics via the backend API,
                using publicly available datasets from institutions such as:
              </Typography>
              <ul>
                <li>
                  Kenya National Bureau of Statistics (KNBS) – Agriculture and Food Security Statistics
                </li>
                <li>
                  National Cereals and Produce Board (NCPB) – Strategic grain reserve and reference market prices
                </li>
                <li>
                  Food and Agriculture Organization of the United Nations (FAO) – FAOSTAT and GIEWS databases
                </li>
                <li>
                  World Bank Open Data – Agriculture & Rural Development Indicators
                </li>
                <li>
                  Kenya Ministry of Agriculture and Livestock Development (MoALD) publications and bulletins
                </li>
              </ul>
              {sourceCitations.length > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Additional source notes:
                  </Typography>
                  <ul>
                    {Array.from(new Set(sourceCitations)).map((citation, index) => (
                      <li key={index}>{citation}</li>
                    ))}
                  </ul>
                </>
              )}
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default AgriDashboard; 
